import { useRef, useState } from "react";

export const SELECT_TYPE = "CustomFields::Select";

export const QUESTION_TYPES = [
  { value: "CustomFields::Text", label: "Short answer" },
  { value: "CustomFields::TextArea", label: "Long answer" },
  { value: SELECT_TYPE, label: "Dropdown" },
  { value: "CustomFields::Checkbox", label: "Checkbox" },
  { value: "CustomFields::Number", label: "Number" },
  { value: "CustomFields::Email", label: "Email" },
  { value: "CustomFields::Date", label: "Date" },
];

const toRow = (customField) => ({
  uid: `saved-${customField.id}`,
  id: customField.id,
  name: customField.name || "",
  type: customField.type,
  required: !!customField.required,
  enabled: customField.enabled ?? true,
  answered: !!customField.answered,
  options: (customField.custom_field_options || []).map((option) => ({
    uid: `saved-option-${option.id}`,
    id: option.id,
    name: option.name || "",
  })),
  removedOptionIds: [],
});

const hasDuplicates = (names) => new Set(names).size !== names.length;

export default function useCheckoutQuestions() {
  const sequence = useRef(0);
  const [rows, setRows] = useState([]);
  const [removedIds, setRemovedIds] = useState([]);

  const nextUid = () => {
    sequence.current += 1;
    return `new-${sequence.current}`;
  };

  const updateRow = (uid, update) =>
    setRows((current) => current.map((row) => (row.uid === uid ? update(row) : row)));

  const reset = (customFields = []) => {
    setRows(customFields.map(toRow));
    setRemovedIds([]);
  };

  const addQuestion = () =>
    setRows((current) => [
      ...current,
      {
        uid: nextUid(),
        id: null,
        name: "",
        type: QUESTION_TYPES[0].value,
        required: false,
        enabled: true,
        answered: false,
        options: [],
        removedOptionIds: [],
      },
    ]);

  const updateQuestion = (uid, changes) => updateRow(uid, (row) => ({ ...row, ...changes }));

  const removeQuestion = (uid) => {
    const row = rows.find((candidate) => candidate.uid === uid);
    if (!row || row.answered) return;

    if (row.id) setRemovedIds((ids) => [...ids, row.id]);
    setRows((current) => current.filter((candidate) => candidate.uid !== uid));
  };

  const addOption = (uid) =>
    updateRow(uid, (row) => ({ ...row, options: [...row.options, { uid: nextUid(), id: null, name: "" }] }));

  const updateOption = (uid, optionUid, name) =>
    updateRow(uid, (row) => ({
      ...row,
      options: row.options.map((option) => (option.uid === optionUid ? { ...option, name } : option)),
    }));

  const removeOption = (uid, optionUid) =>
    updateRow(uid, (row) => {
      const option = row.options.find((candidate) => candidate.uid === optionUid);
      return {
        ...row,
        options: row.options.filter((candidate) => candidate.uid !== optionUid),
        removedOptionIds: option && option.id ? [...row.removedOptionIds, option.id] : row.removedOptionIds,
      };
    });

  const duplicateNames = hasDuplicates(rows.map((row) => row.name.trim()));

  const valid =
    !duplicateNames &&
    rows.every((row) => {
      if (row.name.trim() === "") return false;
      if (row.type !== SELECT_TYPE) return true;

      const optionNames = row.options.map((option) => option.name.trim());
      return optionNames.length > 0 && optionNames.every((name) => name !== "") && !hasDuplicates(optionNames);
    });

  const attributes = () => [
    ...rows.map((row) => ({
      id: row.id || undefined,
      name: row.name.trim(),
      ...(row.id ? {} : { type: row.type }),
      required: row.required,
      enabled: row.enabled,
      custom_field_options_attributes: row.type === SELECT_TYPE
        ? [
          ...row.options.map((option, index) => ({
            id: option.id || undefined,
            name: option.name.trim(),
            position: index + 1,
          })),
          ...row.removedOptionIds.map((id) => ({ id, _destroy: "true" })),
        ]
        : [],
    })),
    ...removedIds.map((id) => ({ id, _destroy: "true" })),
  ];

  return {
    rows,
    reset,
    addQuestion,
    updateQuestion,
    removeQuestion,
    addOption,
    updateOption,
    removeOption,
    duplicateNames,
    valid,
    attributes,
  };
}
